import { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
} from 'recharts';

import { Button } from 'components/ui/button';
import type { SnapshotRadar as SnapshotRadarData } from 'services/api';

const formatDate = (value: string | null) => {
  if (!value) return 'not rated yet';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(value));
};

export const SnapshotRadar = ({ radar }: { radar: SnapshotRadarData | null }) => {
  const [showStartingPoint, setShowStartingPoint] = useState(false);
  const [showMobileScores, setShowMobileScores] = useState(false);
  const chartData = useMemo(() => (radar?.axes ?? []).map((axis) => ({
    ...axis,
    current: axis.latest_value ?? 0,
    starting: axis.initial_value ?? 0,
  })), [radar]);
  const ratedAxes = chartData.filter((axis) => axis.latest_value !== null);
  const ratedCount = ratedAxes.length;
  const missingAxes = chartData.filter((axis) => axis.latest_value === null);
  const max = radar?.definition.scale_max ?? 7;
  const hasRatings = ratedCount > 0;
  const hasStartingPoint = chartData.some((axis) => axis.initial_value !== null);
  const lastRatingAt = chartData.reduce<string | null>((latest, axis) => {
    if (!axis.last_updated_at) return latest;
    return !latest || new Date(axis.last_updated_at) > new Date(latest) ? axis.last_updated_at : latest;
  }, null);
  const textAlternative = chartData
    .map((axis) => `${axis.label}: ${axis.latest_value === null ? 'not rated' : `${axis.latest_value} of ${max}`}`)
    .join('; ');

  const scoreList = (label: string) => (
    <div className="snapshot-radar-card__scores">
      <ul aria-label={label}>
        {(hasRatings ? ratedAxes : chartData).map((axis) => {
          const delta = axis.latest_value !== null && axis.initial_value !== null
            ? Math.round((axis.latest_value - axis.initial_value) * 10) / 10
            : null;
          const percent = axis.latest_value === null ? 0 : Math.max(0, Math.min(100, (axis.latest_value / max) * 100));
          return (
            <li key={axis.key}>
              <span className="snapshot-radar-score__measure">
                <span>{axis.label}</span>
                <span aria-hidden="true"><i style={{ width: `${percent}%` }} /></span>
              </span>
              <span className={`snapshot-radar-card__delta ${delta && delta < 0 ? 'is-negative' : ''}`} aria-label={delta === null ? 'No starting comparison' : `${delta > 0 ? 'up ' : delta < 0 ? 'down ' : ''}${Math.abs(delta)} from starting point`}>
                {delta === null || delta === 0 ? '' : `${delta > 0 ? '↑' : '↓'}${Math.abs(delta)}`}
              </span>
              <strong>{axis.latest_value === null ? `— / ${max}` : `${axis.latest_value} / ${max}`}</strong>
            </li>
          );
        })}
      </ul>
      {missingAxes.length && hasRatings ? <div className="snapshot-radar-unrated"><span>Not rated yet</span>{missingAxes.map((axis) => <p key={axis.key}>{axis.label}</p>)}</div> : null}
    </div>
  );

  return (
    <div className="snapshot-pillars-layout">
      <article className={`snapshot-card snapshot-radar-card ${hasRatings ? '' : 'snapshot-radar-card--empty'}`}>
        <header className="snapshot-radar-card__header">
          <h3>Ten Pillars</h3>
          <p>{ratedCount} of {chartData.length} rated{lastRatingAt ? ` · Last rated ${formatDate(lastRatingAt)}` : ''}</p>
        </header>

        {hasRatings ? (
          <>
            <div
              className="snapshot-radar-chart"
              role="img"
              aria-label={`Pillar ratings. ${textAlternative}`}
            >
              <ResponsiveContainer width="100%" height="100%" debounce={100}>
                <RadarChart data={chartData} cx="50%" cy="50%" outerRadius="67%" margin={{ top: 30, right: 42, bottom: 30, left: 42 }}>
                  <PolarGrid stroke="var(--foundation-purplepurple-500)" strokeOpacity={0.15} />
                  <PolarRadiusAxis domain={[0, max]} tick={false} axisLine={false} />
                  <PolarAngleAxis
                    dataKey="label"
                    tick={({ x, y, payload, textAnchor }) => {
                      const words = String(payload.value).split(' ');
                      const lines: string[] = [];
                      words.forEach((word) => {
                        const last = lines.at(-1);
                        if (!last || `${last} ${word}`.length > 14) lines.push(word);
                        else lines[lines.length - 1] = `${last} ${word}`;
                      });
                      return (
                        <g transform={`translate(${x},${y})`}>
                          {lines.map((line, index) => (
                            <text key={`${line}-${index}`} x={0} y={(index - (lines.length - 1) / 2) * 13} textAnchor={textAnchor} fontSize={11} fill="currentColor">
                              {line}
                            </text>
                          ))}
                        </g>
                      );
                    }}
                  />
                  {showStartingPoint ? (
                    <Radar name="Starting point" dataKey="starting" stroke="rgba(253,105,71,.8)" fill="transparent" strokeDasharray="7 6" strokeWidth={2.5} isAnimationActive={false} />
                  ) : null}
                  <Radar name="Current" dataKey="current" stroke="var(--foundation-purplepurple-500)" fill="var(--foundation-purplepurple-500)" fillOpacity={0.13} strokeWidth={3} isAnimationActive={false} />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            <button type="button" className="snapshot-mobile-scores-toggle" aria-expanded={showMobileScores} onClick={() => setShowMobileScores((shown) => !shown)}>
              <span>{showMobileScores ? 'Hide all scores' : `All ${ratedCount} scores`}</span><ChevronDown aria-hidden="true" />
            </button>
            {showMobileScores ? <div className="snapshot-mobile-scores">{scoreList('Pillar score list')}</div> : null}

            <div className="snapshot-radar-card__actions">
              {hasStartingPoint ? <button type="button" aria-pressed={showStartingPoint} onClick={() => setShowStartingPoint((shown) => !shown)}>{showStartingPoint ? 'Hide starting point' : 'Compare with starting point'}</button> : null}
              <Button asChild size="auto"><Link to="/training">Update ratings</Link></Button>
            </div>
            {showStartingPoint ? <p className="snapshot-radar-card__legend"><span aria-hidden="true" />Starting point · your first ratings</p> : null}
          </>
        ) : (
          <div className="snapshot-radar-empty-content">
            <div aria-hidden="true" className="snapshot-radar-empty-shape" />
            <p>Rate your pillars to see the shape of this season. It takes about two minutes, and everything else on this page keeps working without it.</p>
            <Button asChild size="auto"><Link to="/training">Rate your pillars</Link></Button>
          </div>
        )}
      </article>

      <article className="snapshot-card snapshot-score-card">
        <h3>Your scores</h3>
        {scoreList('Pillar score list')}
      </article>
    </div>
  );
};
